import React, { useEffect, useState } from "react";
import axios from "axios";
import { Grid, Typography, Box } from "@mui/material";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  ResponsiveContainer, Legend, LineChart, Line,
} from "recharts";
import DashboardCard from "../DashboardCard";
import { BASE_URL } from "app/services/auth-services";
import FourthChart from "app/pages/hr/HrOperation/ChartFile/FourthChart";
import useResponsive from "app/pages/useResponsive";
import TwoChart from "app/pages/hr/HrOperation/ChartFile/TwoChart";
import { lastYearDate, todayDate } from "app/shared/ReuseComponents/DateFormatter";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";

const COLORS = ["#ff5e57", "#28c76f", "#ffc107", "#00bcd4"];

const sampleData = {
  total_employees: 150,
  attrition_rate_percentage: 12.5,
  departments: [
    { department: 'Engineering', total_employees_count: 42, resigned_employees_count: 15 },
    { department: 'Marketing', total_employees_count: 28, resigned_employees_count: 15 },
    { department: 'Sales', total_employees_count: 35, resigned_employees_count: 15 },
    { department: 'HR', total_employees_count: 15, resigned_employees_count: 1 },
    { department: 'Finance', total_employees_count: 22, resigned_employees_count: 15 },
  ],
  departmentData: [
    { department: 'Left', resigned_employees_count: 15 },
    { department: 'Current', resigned_employees_count: 135 }
  ],
  monthly_hiring: [
    { month: 'Jan', hires: 5 },
    { month: 'Feb', hires: 8 },
    { month: 'Mar', hires: 6 },
    { month: 'Apr', hires: 10 },
    { month: 'May', hires: 7 },
    { month: 'Jun', hires: 12 },
  ],
  leave_requests: [
    { name: 'Approved', value: 40 },
    { name: 'Pending', value: 10 },
    { name: 'Rejected', value: 5 },
  ],
  gender_distribution: [
    { name: 'Male', value: 90 },
    { name: 'Female', value: 55 },
    { name: 'Other', value: 5 },
  ]
};


const HRDashboard = () => {
  const token = localStorage.getItem("accesstoken");

  const header = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  };
  const companyId = parseInt(localStorage.getItem("OrganizationId"));
  const partnerId = parseInt(localStorage.getItem("PartnerId"));
  const userId = parseInt(localStorage.getItem("UserId"));

  let userDetails = localStorage.getItem("UserDetails");
  userDetails = JSON.parse(userDetails);

  let isMobile = useResponsive("down", "sm");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const payload = {
      start_date:  lastYearDate,
      end_date: todayDate,
      company_id: companyId,
      // user_id: userId, // Uncomment if needed
    };

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [response, depresponse, attritionResponse, exitsResponse] = await Promise.allSettled([
          axios.post(`${BASE_URL}/Felix/TotalEmployees/`, payload, header),
          axios.post(`${BASE_URL}/Felix/DepartmentWiseAttritionRate/`, payload, header),
          axios.post(`${BASE_URL}/Felix/AttritionRateSummary/`, payload, header),
          axios.post(`${BASE_URL}/Felix/EmployeesUpcomingExits/`, payload, header),
        ]);

        const mainData = response.value?.data?.results;
        const departmentData = depresponse.value?.data?.results;
        const attritionSummary = attritionResponse.value?.data;
        const upcomingExists = exitsResponse.value?.data;

        // Check if essential data is available
        if (mainData?.total_employees_count) {
          setData({
            ...mainData,
            departments: departmentData, // Merges department-wise data
            attritionSummary: attritionSummary,
            upcomingExists: upcomingExists,
          });
        } else {
          console.warn("Incomplete data received. Falling back to sample data.");
          setData(sampleData);
        }

      } catch (err) {
        console.error("API request failed. Using sample data as fallback.", err);
        setData(sampleData);
        setError("Live data unavailable. Showing sample data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  
  const CardWrapper = ({ children }) => (
  <Box
    sx={{
      /* makes card shorter */
      "& .MuiCard-root": {
        height: 110,
        borderRadius: "14px",

        /* compress inner layout */
        display: "flex",
        alignItems: "center",

        /* reduce internal spacing visually */
        transform: "scale(0.92)",
        transformOrigin: "center",
      },

      /* tighten icon box */
      "& .MuiCard-root > div:first-of-type": {
        width: 44,
        height: 44,
      },

      /* reduce big number look */
      "& .MuiCard-root h1, & .MuiCard-root h2, & .MuiCard-root h3, & .MuiCard-root h4": {
        fontSize: "22px !important",
        lineHeight: 1.1,
      },

      /* reduce label spacing */
      "& .MuiTypography-root": {
        margin: 0,
      },
    }}
  >
    {children}
  </Box>
);


  console.log("DATA", data)

  if (loading) return <div>Loading...</div>;
  if (!data) return null;

  return (
    <Box
  sx={{
    px: { xs: 2, sm: 3, md: 4 },
    py: 2,
    maxWidth: 1600,
    margin: "0 auto",
  }}
>
  <Typography
    variant="h4"
    sx={{
      fontWeight: 600,
      mb: 3,
      color: "#101828",
      letterSpacing: ".3px"
    }}
  >
    HR Dashboard
  </Typography>
      <br />
      {error && (
        <Typography variant="body2" color="warning.main" mb={2}>
          ⚠️ {error}
        </Typography>
      )}
<Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
      md: "repeat(4, 1fr)",
    },
    gap: 2.5,
    mb: 3,
    alignItems: "stretch",
  }}
><CardWrapper>
  <DashboardCard
    title="Total Employees"
    value={data?.total_employees_count}
    color="#6366F1"
    icon={<GroupsRoundedIcon />}
  />
</CardWrapper>
<CardWrapper>
  <DashboardCard
    title="Departments"
    value={data?.departments?.length}
    color="#10B981"
    icon={<ApartmentRoundedIcon />}
  />
</CardWrapper>
<CardWrapper>
  <DashboardCard
    title="Upcoming Exits"
    value={data?.upcomingExists?.employees_upcomingexits_count}
    color="#F59E0B"
    icon={<LogoutRoundedIcon />}
  />
</CardWrapper>
<CardWrapper>
  <DashboardCard
    title="Attrition Rate"
    value={`${data?.attritionSummary?.attrition_rate_percentage}%`}
    color="#EF4444"
    icon={<TrendingDownRoundedIcon />}
  />
</CardWrapper>
</Box>

      <Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "1fr 1fr",
    },
    gap: 3,
  }}
>

          {/* Department-wise Employees - Bar Chart */}
          <Grid item xs={12} md={6}>
            <ChartBox title="Department Wise Employees">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data?.departments}>
                  <XAxis dataKey="department" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total_employees_count" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </ChartBox>
          </Grid>

          {/* Attrition Overview - Pie Chart */}
          <Grid item xs={12} md={6}>
            <ChartBox title="Attrition Overview">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.departments}
                    dataKey="resigned_employees_count"
                    nameKey="department"
                    outerRadius={100}
                    label
                  >
                    {data.departments?.map((entry, index) => (
                      <Cell key={`attrition-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartBox>
          </Grid>

          {/* Monthly Hiring Trend - Line Chart */}
          <Grid item xs={12} md={6}>
            <ChartBox title="">
              {/* <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.monthly_hiring}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="hires" stroke="#82ca9d" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer> */}

              <TwoChart
                isMobile={isMobile}
                businessUnit={userDetails?.Business_Unit}
                partnerName={partnerId}
              />
            </ChartBox>
          </Grid>

          {/* Gender Distribution - Pie Chart */}
          <Grid item xs={12} md={6}>
            <ChartBox title="">
              <ResponsiveContainer width="100%" height={300}>
                {/* <PieChart>
                  <Pie
                    data={data.gender_distribution}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label
                  >
                    {data.gender_distribution?.map((entry, index) => (
                      <Cell key={`gender-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart> */}

                <FourthChart
                  isMobile={isMobile}
                  businessUnit={userDetails?.Business_Unit}
                  partnerName={partnerId}
                />
              </ResponsiveContainer>
            </ChartBox>
          </Grid>

    
      </Box>
    </Box>
  );
};

// Reusable chart container box
const ChartBox = ({ title, children }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 3,
      // minWidth: 380,
      width: "100%",
      minHeight: 360,
      border: "1px solid #EAECF0",
      height: "100%",
      overflowY: "auto",
      backgroundColor: "background.paper",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.09)",
      transition: "box-shadow 0.3s ease",
      '&:hover': {
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
      },
    }}
  >
    <Typography variant="h4" fontWeight={500} gutterBottom>{title}</Typography>
    {children}
  </Box>
);

export default HRDashboard;
